import {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
	useCallback,
} from "react";
import type { TenantBranding } from "@/api/chain";
import { chainApi } from "@/api/chain";
import { applyBrandingCssVars } from "@/lib/tenantBranding";
import type { DoctorTeamBranding } from "@/api/doctorTeam";
import { readJwtUserClaims } from "@/lib/jwtClaims";

function mergeUserWithTokenClaims(user: User, token: string): User {
	const claims = readJwtUserClaims(token);
	return {
		...user,
		// JWT is authoritative for tenancy (avoids stale localStorage showing chain scope while API uses sub-admin token).
		adminScope: claims.adminScope ?? user.adminScope,
		groupId: claims.groupId ?? user.groupId,
		hospitalId: claims.hospitalId ?? user.hospitalId,
	};
}

export type AdminScope = "CHAIN" | "HOSPITAL";

export type HospitalOption = { id: string; name: string };

interface User {
	id: string;
	email: string;
	name: string;
	role:
		| "SUPER_ADMIN"
		| "HOSPITAL_ADMIN"
		| "DOCTOR"
		| "RMO"
		| "NURSE"
		| "RECEPTIONIST"
		| "OPERATIONS_MANAGER"
		| "SALES_PERSON"
		| "SALES_MANAGER"
		| "EXTERNAL_PARTNER"
		| "LAB_TECHNICIAN"
		| "PHARMACIST"
		| "CHARTERED_ACCOUNTANT"
		| "INVESTOR"
		| "VENDOR"
		| "PHARMA_VENDOR"
		| "BUSINESS_ANALYST"
		| "DOCTOR_TEAM_LEAD";
	hospitalId?: string;
	doctorTeamLeadId?: string;
	teamBranding?: DoctorTeamBranding;
	adminScope?: AdminScope;
	groupId?: string;
	groupName?: string;
	hospitals?: HospitalOption[];
	branding?: TenantBranding;
	specialisation?: string;
	isLeadAgency?: boolean;
	investorReportKeys?: string[];
}

interface AuthContextType {
	isAuthenticated: boolean;
	user: User | null;
	isLoading: boolean;
	activeHospitalId: string | null;
	branding: TenantBranding | null;
	login: (token: string, user: User) => void;
	logout: () => void;
	switchHospital: (hospitalId: string) => Promise<void>;
	/** Switch active hospital without a new JWT (e.g. chartered accountant multi-site). */
	setActiveHospitalLocal: (hospitalId: string) => void;
	refreshBranding: (hospitalId?: string | null) => Promise<void>;
	isChainAdmin: boolean;
}

const defaultContextValue: AuthContextType = {
	isAuthenticated: false,
	user: null,
	isLoading: true,
	activeHospitalId: null,
	branding: null,
	login: () => {},
	logout: () => {},
	switchHospital: async () => {},
	setActiveHospitalLocal: () => {},
	refreshBranding: async () => {},
	isChainAdmin: false,
};

const AuthContext = createContext<AuthContextType>(defaultContextValue);

function teamBrandingToCss(branding: DoctorTeamBranding | null | undefined) {
	if (!branding?.customUiColors) {
		applyBrandingCssVars(null);
		return;
	}
	applyBrandingCssVars({
		logoUrl: branding.logoUrl,
		primaryColor: branding.primaryColor,
		secondaryColor: branding.secondaryColor,
		accentColor: branding.accentColor,
		sidebarBgColor: branding.sidebarBgColor,
		customUiColors: true,
	});
}

function applyBrandingCss(branding: TenantBranding | null | undefined) {
	applyBrandingCssVars(branding);
}

export function AuthProvider({ children }: { children: ReactNode }) {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [user, setUser] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [activeHospitalId, setActiveHospitalId] = useState<string | null>(null);
	const [branding, setBranding] = useState<TenantBranding | null>(null);

	const isChainAdmin =
		user?.role === "HOSPITAL_ADMIN" &&
		(user?.adminScope === "CHAIN" ||
			(!!user?.groupId && (user?.hospitals?.length ?? 0) > 1));

	const refreshBranding = useCallback(async (hospitalId?: string | null) => {
		const hid = hospitalId ?? activeHospitalId;
		if (!hid) return;
		try {
			const r = await chainApi.getBranding(hid);
			const effective = r.data?.data?.effective as TenantBranding | undefined;
			if (effective) {
				setBranding(effective);
				applyBrandingCss(effective);
				setUser((prev) => {
					if (!prev) return prev;
					const next = { ...prev, branding: effective };
					localStorage.setItem("user", JSON.stringify(next));
					return next;
				});
			}
		} catch {
			/* optional */
		}
	}, [activeHospitalId]);

	useEffect(() => {
		const initializeAuth = async () => {
			try {
				const token = localStorage.getItem("token");
				const userData = localStorage.getItem("user");
				const storedHospital = localStorage.getItem("activeHospitalId");

				if (token && userData) {
					const parsedUser = mergeUserWithTokenClaims(
						JSON.parse(userData) as User,
						token
					);
					const hospitalId =
						storedHospital ||
						parsedUser.hospitalId ||
						parsedUser.hospitals?.[0]?.id ||
						null;
					setIsAuthenticated(true);
					setUser(parsedUser);
					localStorage.setItem("user", JSON.stringify(parsedUser));
					setActiveHospitalId(hospitalId);
					if (parsedUser.teamBranding?.customUiColors) {
						teamBrandingToCss(parsedUser.teamBranding);
					} else if (parsedUser.branding) {
						setBranding(parsedUser.branding);
						applyBrandingCss(parsedUser.branding);
					} else if (hospitalId) {
						await refreshBranding(hospitalId);
					}
				} else {
					setIsAuthenticated(false);
					setUser(null);
				}
			} catch {
				localStorage.removeItem("token");
				localStorage.removeItem("user");
				localStorage.removeItem("activeHospitalId");
				setIsAuthenticated(false);
				setUser(null);
			} finally {
				setIsLoading(false);
			}
		};

		initializeAuth();
	}, [refreshBranding]);

	const login = (token: string, userData: User) => {
		const mergedUser = mergeUserWithTokenClaims(userData, token);
		localStorage.setItem("token", token);
		localStorage.setItem("user", JSON.stringify(mergedUser));
		const hospitalId =
			userData.hospitalId || userData.hospitals?.[0]?.id || null;
		if (hospitalId) {
			localStorage.setItem("activeHospitalId", hospitalId);
			setActiveHospitalId(hospitalId);
		}
		if (userData.teamBranding?.customUiColors) {
			teamBrandingToCss(userData.teamBranding);
		} else if (userData.branding) {
			setBranding(userData.branding);
			applyBrandingCss(userData.branding);
		}
		setIsAuthenticated(true);
		setUser(mergedUser);
	};

	const logout = () => {
		localStorage.removeItem("token");
		localStorage.removeItem("user");
		localStorage.removeItem("activeHospitalId");
		setIsAuthenticated(false);
		setUser(null);
		setActiveHospitalId(null);
		setBranding(null);
		applyBrandingCss(null);
	};

	const setActiveHospitalLocal = (hospitalId: string) => {
		localStorage.setItem("activeHospitalId", hospitalId);
		setActiveHospitalId(hospitalId);
	};

	const switchHospital = async (hospitalId: string) => {
		const r = await chainApi.switchHospital(hospitalId);
		const { token, user: nextUser } = r.data.data as {
			token: string;
			user: User;
		};
		const mergedUser = mergeUserWithTokenClaims(nextUser, token);
		localStorage.setItem("token", token);
		localStorage.setItem("user", JSON.stringify(mergedUser));
		localStorage.setItem("activeHospitalId", hospitalId);
		setUser(mergedUser);
		setActiveHospitalId(hospitalId);
		if (nextUser.branding) {
			setBranding(nextUser.branding);
			applyBrandingCss(nextUser.branding);
		} else {
			await refreshBranding(hospitalId);
		}
	};

	const value = {
		isAuthenticated,
		user,
		isLoading,
		activeHospitalId,
		branding,
		login,
		logout,
		switchHospital,
		setActiveHospitalLocal,
		refreshBranding,
		isChainAdmin,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
